import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section id="categories" className="scroll-mt-16">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-stone-900 md:text-3xl">{t('categories.title')}</h2>
          <p className="mt-3 text-sm text-stone-500 md:text-base">{t('categories.subtitle')}</p>
        </div>

        {/* Dropdown */}
        <div ref={dropdownRef} className="relative mx-auto mt-8 max-w-md">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-md border border-stone-200 bg-white px-4 py-3 text-sm font-medium text-stone-800 hover:border-stone-300 whitespace-nowrap cursor-pointer"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="flex items-center gap-2">
              <i className="ri-list-check-2 w-5 h-5 flex items-center justify-center text-emerald-600" aria-hidden="true" />
              {t('categories.dropdownLabel')}
            </span>
            <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-stone-400" aria-hidden="true" />
          </button>

          {open && (
            <ul
              role="listbox"
              className="absolute left-0 right-0 z-30 mt-2 max-h-80 overflow-y-auto rounded-lg border border-stone-200 bg-white py-1"
            >
              {categories.map((cat) => (
                <li key={cat.key}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-between px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 whitespace-nowrap"
                  >
                    <span>{t(`categories.items.${cat.key}`)}</span>
                    <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-stone-400" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Category cards */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {categories.map((cat) => (
            <a
              key={cat.key}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group relative block overflow-hidden rounded-lg border border-stone-200 bg-white"
            >
              <div className="aspect-[4/3] w-full overflow-hidden">
                <img
                  src={cat.image}
                  alt={t(`categories.items.${cat.key}`)}
                  title={t(`categories.items.${cat.key}`)}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-3 pb-3 pt-8">
                <span className="text-sm font-semibold text-white whitespace-nowrap">
                  {t(`categories.items.${cat.key}`)}
                </span>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-stone-800">
                  <i className="ri-arrow-right-up-line w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}